import { createContext, useContext, ReactNode } from 'react'
import { WagmiProvider, http } from 'wagmi'
import { mainnet, polygon, base, sepolia } from 'wagmi/chains'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RainbowKitProvider, getDefaultConfig, darkTheme } from '@rainbow-me/rainbowkit'
import { useAccount, useBalance } from 'wagmi'
import '@rainbow-me/rainbowkit/styles.css'

// Get WalletConnect project ID - required for production
const projectId = import.meta.env.VITE_WALLET_CONNECT_PROJECT_ID
if (!projectId && import.meta.env.PROD) {
  throw new Error('VITE_WALLET_CONNECT_PROJECT_ID is required in production')
}

// Create wagmi config
const config = getDefaultConfig({
  appName: 'FreakMe.Fun',
  projectId: projectId || 'dev-fallback-id',
  chains: [mainnet, polygon, base, sepolia],
  transports: {
    [mainnet.id]: http(),
    [polygon.id]: http(),
    [base.id]: http(),
    [sepolia.id]: http(),
  },
})

// Create query client
const queryClient = new QueryClient()

interface WalletContextType {
  address: `0x${string}` | undefined
  isConnected: boolean
  balance: string | undefined
}

const WalletContext = createContext<WalletContextType | undefined>(undefined)

// Inner component that uses wagmi hooks
function WalletContextProvider({ children }: { children: ReactNode }) {
  const { address, isConnected } = useAccount()
  const { data: balanceData } = useBalance({ address })
  
  const balance = balanceData ? String(balanceData.value) : undefined
  
  return (
    <WalletContext.Provider value={{ address, isConnected, balance }}>
      {children}
    </WalletContext.Provider>
  )
}

export function WalletProvider({ children }: { children: ReactNode }) {
  return (
    <WagmiProvider config={config}>
      <QueryClientProvider client={queryClient}>
        <RainbowKitProvider
          theme={darkTheme({
            accentColor: '#ff3366',
            accentColorForeground: 'white',
            borderRadius: 'medium',
            fontStack: 'system',
          })}
          modalSize="compact"
        >
          <WalletContextProvider>
            {children}
          </WalletContextProvider>
        </RainbowKitProvider>
      </QueryClientProvider>
    </WagmiProvider>
  )
}

export function useWallet() {
  const context = useContext(WalletContext)
  if (context === undefined) {
    throw new Error('useWallet must be used within a WalletProvider')
  }
  return context
}
